import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Button,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Chip,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, startOfMonth, endOfMonth, addMonths } from 'date-fns';

// Import components
import PageHeader from '../../components/ui/PageHeader';
import { Card, MagicLoader } from '../../design-system';
import { FormPageHeader } from '../../components/ui/FormFields';
import { FormSection } from '../../design-system/components/forms';

// Import stores
import usePayrollStore from '../../stores/payrollStore';
import useHRStore from '../../stores/hrStore';
import useNotificationStore from '../../stores/notificationStore';

const steps = ['Basic Information', 'Select Employees', 'Review & Adjustments'];

const PayrollRunForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const {
    currentPayrollRun,
    fetchPayrollRun,
    createPayrollRun,
    updatePayrollRun,
    addPayrollAdjustment,
    isLoading,
  } = usePayrollStore();
  const { employees, fetchEmployees } = useHRStore();
  const { showSuccess, showError } = useNotificationStore();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    periodStart: startOfMonth(new Date()),
    periodEnd: endOfMonth(new Date()),
    description: '',
    selectedEmployees: [],
    adjustments: {},
  });

  // Fetch employees
  useEffect(() => {
    fetchEmployees({ 
      'employment.status': 'active',
      limit: 1000 
    });
  }, [fetchEmployees]);

  // Fetch payroll run if editing
  useEffect(() => {
    if (isEdit && id) {
      fetchPayrollRun(id).then((data) => {
        setFormData({
          month: data.month,
          year: data.year,
          periodStart: new Date(data.periodStart),
          periodEnd: new Date(data.periodEnd),
          description: data.description || '',
          selectedEmployees: data.employees.map(e => e.employee._id),
          adjustments: data.employees.reduce((acc, emp) => {
            acc[emp.employee._id] = emp.adjustments || [];
            return acc;
          }, {}),
        });
      });
    }
  }, [isEdit, id, fetchPayrollRun]);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    try {
      const payrollData = {
        month: formData.month,
        year: formData.year,
        periodStart: formData.periodStart,
        periodEnd: formData.periodEnd,
        description: formData.description,
        employees: formData.selectedEmployees.map(empId => {
          const employee = employees.find(e => e._id === empId);
          return {
            employee: empId,
            basicSalary: employee?.compensation?.salary?.amount || 0,
            adjustments: formData.adjustments[empId] || [],
          };
        }),
      };

      if (isEdit) {
        await updatePayrollRun(id, payrollData);
        showSuccess('Payroll run updated successfully');
      } else {
        const result = await createPayrollRun(payrollData);
        showSuccess('Payroll run created successfully');
        navigate(`/payroll/runs/${result._id}`);
      }
    } catch (error) {
      showError(error.message || 'Failed to save payroll run');
    }
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      selectedEmployees: prev.selectedEmployees.includes(employeeId)
        ? prev.selectedEmployees.filter(id => id !== employeeId)
        : [...prev.selectedEmployees, employeeId]
    }));
  };

  const handleSelectAllEmployees = (event) => {
    if (event.target.checked) {
      setFormData(prev => ({
        ...prev,
        selectedEmployees: employees.map(emp => emp._id)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedEmployees: []
      }));
    }
  };

  const handleAddAdjustment = (employeeId) => {
    const newAdjustment = {
      type: 'addition',
      name: '',
      amount: 0,
      description: '',
    };

    setFormData(prev => ({
      ...prev,
      adjustments: {
        ...prev.adjustments,
        [employeeId]: [...(prev.adjustments[employeeId] || []), newAdjustment]
      }
    }));
  };

  const handleUpdateAdjustment = (employeeId, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      adjustments: {
        ...prev.adjustments,
        [employeeId]: prev.adjustments[employeeId].map((adj, i) => 
          i === index ? { ...adj, [field]: value } : adj
        )
      }
    }));
  };

  const handleRemoveAdjustment = (employeeId, index) => {
    setFormData(prev => ({
      ...prev,
      adjustments: {
        ...prev.adjustments,
        [employeeId]: prev.adjustments[employeeId].filter((_, i) => i !== index)
      }
    }));
  };

  const calculateEmployeeSalary = (employee) => {
    const basicSalary = employee.compensation?.salary?.amount || 0;
    const adjustments = formData.adjustments[employee._id] || [];
    
    const totalAdjustments = adjustments.reduce((sum, adj) => {
      return sum + (adj.type === 'addition' ? adj.amount : -adj.amount);
    }, 0);

    return basicSalary + totalAdjustments;
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  if (isLoading) {
    return <MagicLoader />;
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <FormSection title="Payroll Period" subtitle="Define the payroll period and details">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                    label="Month"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {format(new Date(2024, i), 'MMMM')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Year"
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  inputProps={{ min: 2020, max: 2030 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Period Start"
                    value={formData.periodStart}
                    onChange={(date) => setFormData({ ...formData, periodStart: date })}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Period End"
                    value={formData.periodEnd}
                    onChange={(date) => setFormData({ ...formData, periodEnd: date })}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add any notes or description for this payroll run"
                />
              </Grid>
            </Grid>
          </FormSection>
        );

      case 1:
        return (
          <FormSection title="Select Employees" subtitle="Choose employees to include in this payroll">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          formData.selectedEmployees.length > 0 &&
                          formData.selectedEmployees.length < employees.length
                        }
                        checked={
                          employees.length > 0 &&
                          formData.selectedEmployees.length === employees.length
                        }
                        onChange={handleSelectAllEmployees}
                      />
                    </TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell align="right">Basic Salary</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((employee) => (
                    <TableRow
                      key={employee._id}
                      sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={formData.selectedEmployees.includes(employee._id)}
                          onChange={() => handleEmployeeToggle(employee._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {employee.user?.firstName} {employee.user?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {employee.employeeId}
                        </Typography>
                      </TableCell>
                      <TableCell>{employee.employment?.department}</TableCell>
                      <TableCell>{employee.employment?.position}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(employee.compensation?.salary?.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {formData.selectedEmployees.length} employees selected
              </Typography>
            </Box>
          </FormSection>
        );

      case 2:
        const selectedEmployeesList = employees.filter(emp => 
          formData.selectedEmployees.includes(emp._id)
        );

        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Review employee salaries and add any adjustments (bonuses, deductions, etc.)
            </Alert>
            
            {selectedEmployeesList.map((employee) => (
              <Card key={employee._id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">
                        {employee.user?.firstName} {employee.user?.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {employee.employment?.position} • {employee.employment?.department}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary">
                        Net Salary
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {formatCurrency(calculateEmployeeSalary(employee))}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Basic Salary: {formatCurrency(employee.compensation?.salary?.amount || 0)}
                    </Typography>
                  </Box>

                  {/* Adjustments */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">Adjustments</Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddAdjustment(employee._id)}
                      >
                        Add Adjustment
                      </Button>
                    </Box>

                    {(formData.adjustments[employee._id] || []).map((adjustment, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={adjustment.type}
                            onChange={(e) => handleUpdateAdjustment(employee._id, index, 'type', e.target.value)}
                          >
                            <MenuItem value="addition">Addition</MenuItem>
                            <MenuItem value="deduction">Deduction</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <TextField
                          size="small"
                          label="Name"
                          value={adjustment.name}
                          onChange={(e) => handleUpdateAdjustment(employee._id, index, 'name', e.target.value)}
                          sx={{ flexGrow: 1 }}
                        />
                        
                        <TextField
                          size="small"
                          label="Amount"
                          type="number"
                          value={adjustment.amount}
                          onChange={(e) => handleUpdateAdjustment(employee._id, index, 'amount', parseFloat(e.target.value) || 0)}
                          sx={{ width: 120 }}
                        />
                        
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveAdjustment(employee._id, index)}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <FormPageHeader
        title={isEdit ? 'Edit Payroll Run' : 'New Payroll Run'}
        subtitle={isEdit ? 'Update payroll run details' : 'Create a new payroll run'}
        onBack={() => navigate('/payroll/runs')}
      />

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<BackIcon />}
            >
              Back
            </Button>
            
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={activeStep === steps.length - 1 ? <SaveIcon /> : <NextIcon />}
              disabled={
                (activeStep === 1 && formData.selectedEmployees.length === 0) ||
                isLoading
              }
            >
              {activeStep === steps.length - 1 ? 'Save Payroll Run' : 'Next'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PayrollRunForm;